import {
  Box,
  Button,
  Divider,
  HStack,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import {
  ImagePreview,
  NotificationPreviewType,
} from "../ImagePreview/ImagePreview";
import { InfoIcon } from "../../styles/icons";

const PREVIEW_INFO_TOOLTIP =
  "Preview only. Selecting an option does not change your notification setup.";

interface FormDevicePreviewPanelProps {
  businessDisplayName: string;
  lockScreenMessage: string;
  backOfPassMessage: string | null;
  isGoogleTabDisabled: boolean;
  notificationType?: NotificationPreviewType;
  onNotificationTypeChange?: (value: NotificationPreviewType) => void;
  showNotificationTypeToggle: boolean;
  selectedLocationName?: string | null;
}

export const FormDevicePreviewPanel: React.FC<FormDevicePreviewPanelProps> = ({
  businessDisplayName,
  lockScreenMessage,
  backOfPassMessage,
  isGoogleTabDisabled,
  notificationType = "push",
  onNotificationTypeChange,
  showNotificationTypeToggle,
  selectedLocationName,
}) => {
  const isGeoPreview = notificationType === "geo";
  const previewSectionTitle = isGeoPreview
    ? selectedLocationName
      ? `Geo-Fence Preview · ${selectedLocationName}`
      : "Geo-Fence Preview"
    : "Push Notification Preview";
  const previewSectionSubtitle = isGeoPreview
    ? "Preview the lock-screen notification members see when entering a location."
    : "Preview the lock-screen notification and update on the back of the pass.";

  const renderPillToggleButton = (
    label: string,
    isSelected: boolean,
    onClick: () => void
  ) => (
    <Button
      onClick={onClick}
      borderRadius="full"
      borderWidth="1px"
      borderColor={isSelected ? "gray.200" : "transparent"}
      bg={isSelected ? "white" : "transparent"}
      color={isSelected ? "cherryRed.100" : "gray.700"}
      boxShadow={
        isSelected
          ? "0px 1px 3px rgba(16, 24, 40, 0.12), 0px 1px 2px rgba(16, 24, 40, 0.08)"
          : "none"
      }
      fontWeight={isSelected ? "medium" : "normal"}
      h="40px"
      px={5}
      _hover={{
        bg: isSelected ? "white" : "transparent",
      }}
      _active={{
        bg: isSelected ? "white" : "transparent",
      }}
    >
      {label}
    </Button>
  );

  return (
    <Box
      backgroundColor="white"
      borderRadius="lg"
      boxShadow="md"
      overflow="hidden"
      p={4}
    >
      <VStack w="100%" spacing={4} align="stretch">
        <VStack align="start" spacing={1} w="100%">
          <HStack spacing={2} alignItems="center">
            <Text fontSize="xl" fontWeight="700" color="gray.700">
              Preview
            </Text>
            <Tooltip label={PREVIEW_INFO_TOOLTIP} placement="top" hasArrow>
              <Box
                as="span"
                display="inline-flex"
                alignItems="center"
                color="gray.500"
                cursor="pointer"
              >
                <InfoIcon boxSize={4} />
              </Box>
            </Tooltip>
          </HStack>
          <Text fontSize="sm" color="gray.600">
            See how your notification will appear to members.
          </Text>
        </VStack>

        {showNotificationTypeToggle && onNotificationTypeChange && (
          <VStack align="start" spacing={2} w="100%">
            <Text
              fontSize="xs"
              color="gray.500"
              fontWeight="medium"
              letterSpacing="wide"
              textTransform="uppercase"
            >
              Previewing
            </Text>
            <HStack
              bg="cherryBackground.tag"
              borderRadius="full"
              p={1}
              spacing={0}
              w="fit-content"
              alignSelf="flex-start"
            >
              {renderPillToggleButton(
                "Push Notification",
                notificationType === "push",
                () => onNotificationTypeChange("push")
              )}
              {renderPillToggleButton(
                "Geo-Fence",
                notificationType === "geo",
                () => onNotificationTypeChange("geo")
              )}
            </HStack>
          </VStack>
        )}

        <Divider borderColor="cherryBorder.100" />

        <VStack align="start" spacing={1} w="100%">
          <Text fontSize="lg" fontWeight="700" color="gray.700">
            {previewSectionTitle}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {previewSectionSubtitle}
          </Text>
        </VStack>

        <ImagePreview
          businessDisplayName={businessDisplayName}
          lockScreenMessage={lockScreenMessage}
          backOfPassMessage={backOfPassMessage}
          isGoogleTabDisabled={isGoogleTabDisabled}
          hidePassBack={isGeoPreview}
        />
      </VStack>
    </Box>
  );
};
